/**
 * News and Events Page
 * Institutional publishing hub with filter, featured content, and card grid
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import NewsAndEventsHero from '../components/NewsAndEventsHero';
import NewsEventsFilterBar from '../components/NewsEventsFilterBar';
import FeaturedNews from '../components/FeaturedNews';
import NewsEventsGrid from '../components/NewsEventsGrid';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const AUTH_USERNAME = process.env.REACT_APP_USERNAME || '';
const AUTH_PASSWORD = process.env.REACT_APP_PASSWORD || '';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
};

const mapMediaItem = (item) => ({
  id: String(item.MediaId ?? item.Id ?? item.id ?? Math.random()),
  type: (item.Type ?? item.type ?? 'news').toString().toLowerCase(),
  category: item.Category ?? item.MediaSourceType ?? item.category ?? '',
  title: stripHtml(item.Title ?? item.title ?? ''),
  summary: stripHtml(item.Summary ?? item.Description ?? item.description ?? item.summary ?? ''),
  content: item.Content ?? item.Body ?? item.FullDescription ?? item.Details ?? item.Description ?? '',
  date: formatDate(item.PublishedAt ?? item.CreatedAt ?? item.Date ?? item.date),
  author: item.Author ?? item.CreatedBy ?? item.author ?? '',
  image: item.Image ?? item.ImageUrl ?? item.Link ?? item.image ?? null,
  eventDate: formatDate(item.EventDate ?? item.eventDate),
  location: item.Location ?? item.location ?? '',
  time: item.Time ?? item.time ?? '',
});

const getYouTubeVideoId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const match = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})/);
  return match ? match[1] : null;
};

const mapVideoItem = (item) => {
  const videoUrl = item.Image || '';
  const videoId = getYouTubeVideoId(videoUrl);
  return {
    id: String(item.InfoId ?? item.id ?? Math.random()),
    title: stripHtml(item.Title ?? ''),
    description: stripHtml(item.Description ?? ''),
    videoUrl,
    videoId,
    embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : videoUrl,
    date: formatDate(item.PublishedAt ?? item.Date ?? item.CreatedAt),
  };
};

const fetchMedia = async (type) => {
  const response = await axios.post(
    `${CMS_API_URL}media/by-filter`,
    { CategoryId: 58, Type: type },
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
      auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
    }
  );

  if (response.data?.code === 1 && Array.isArray(response.data?.data)) {
    return response.data.data
      .filter((d) => d.IsDeleted !== true && d.IsPublished === true)
      .map(mapMediaItem);
  }
  return [];
};

const fetchVideos = async () => {
  const response = await axios.post(
    `${CMS_API_URL}info/published`,
    { CategoryId: 63, ParentId: null },
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    }
  );

  if (response.data?.code === 1 && Array.isArray(response.data?.data)) {
    return response.data.data
      .filter((d) => d.IsDeleted !== true && d.IsPublished === true)
      .map(mapVideoItem);
  }
  return [];
};

const NewsAndEventsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All Categories');
  const [activeType, setActiveType] = useState('all');
  const [sortBy, setSortBy] = useState('Latest');

  const [features, setFeatures] = useState([]);
  const [latestUpdates, setLatestUpdates] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(true);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(true);

  useEffect(() => {
    fetchMedia('FEATURES')
      .then((data) => {
        setFeatures(data);
        setLatestUpdates(data);
      })
      .catch((e) => console.error('Failed to fetch news items:', e))
      .finally(() => {
        setLoadingFeatures(false);
        setLoadingLatest(false);
      });

    fetchVideos()
      .then(setVideos)
      .catch((e) => console.error('Failed to fetch videos:', e))
      .finally(() => setLoadingVideos(false));
  }, []);

  // Filter logic applied only to latest updates
  const availableCategories = useMemo(
    () => ['All Categories', ...new Set(latestUpdates.map((i) => i.category).filter(Boolean))],
    [latestUpdates]
  );

  const filteredLatest = useMemo(() => {
    let items = [...latestUpdates];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.summary.toLowerCase().includes(q) ||
          i.author.toLowerCase().includes(q)
      );
    }

    if (activeType !== 'all') {
      items = items.filter((i) => i.type === activeType);
    }

    if (activeCategory !== 'All Categories') {
      items = items.filter((i) => i.category === activeCategory);
    }

    if (sortBy === 'Latest') {
      items.sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (sortBy === 'Oldest') {
      items.sort((a, b) => new Date(a.date) - new Date(b.date));
    }

    return items;
  }, [latestUpdates, searchQuery, activeCategory, activeType, sortBy]);

  const featuredItem = features.length > 0 ? features[0] : null;

  return (
    <div>
      <NewsAndEventsHero />
      <NewsEventsFilterBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        activeType={activeType}
        onTypeChange={setActiveType}
        sortBy={sortBy}
        onSortChange={setSortBy}
        categories={availableCategories}
      />
      {!loadingFeatures && featuredItem && <FeaturedNews item={featuredItem} />}
      {!loadingLatest && <NewsEventsGrid items={filteredLatest} />}
      {!loadingVideos && videos.length > 0 && (
        <section className="bg-white py-8 md:py-10 border-b border-gray-100">
          <div className="container mx-auto px-6 md:px-8 lg:px-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-5 bg-[#ca9a37] rounded-full" />
              <h2 className="text-lg font-bold text-[#2f3951]">Videos</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video) => (
                <div
                  key={video.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="aspect-video bg-gray-100">
                    {video.videoId ? (
                      <iframe
                        src={video.embedUrl}
                        title={video.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full border-0"
                      />
                    ) : (
                      <a
                        href={video.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-full flex items-center justify-center text-[#2f3951] hover:text-[#ca9a37] transition-colors"
                      >
                        <span className="text-sm font-medium">Watch Video</span>
                      </a>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-[#2f3951] mb-1 line-clamp-2">
                      {video.title}
                    </h3>
                    <p className="text-gray-500 text-xs line-clamp-2">
                      {video.description}
                    </p>
                    {video.date && (
                      <span className="text-gray-400 text-[11px] mt-2 block">
                        {video.date}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NewsAndEventsPage;
